import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '@ohif/i18n';
import cn from 'classnames';
import {
  AppNotification,
  formatNotificationContent,
  formatNotificationTimestamp,
  getNotificationNavigationPath,
  getNotificationPatientName,
  NOTIFICATION_PAGE_SIZE,
  NOTIFICATION_RECENT_HOURS,
  NOTIFICATION_RECENT_LIST_PARAMS,
  notificationCategoryLabel,
  notificationKeys,
  notificationService,
  notificationTypeLabel,
} from '../helpers/api/notifications/notifications';
import { markNotificationReadInCache } from '../helpers/api/notifications/notification-handler';
import { Button } from '../UI/ComponnentsUI/common/Button';

export type NotificationInboxVariant = 'sidebar' | 'toolbar' | 'compact';

const PANEL_WIDTH = 360;
const PANEL_HEADER_HEIGHT = 56;
const NOTIFICATION_ITEM_HEIGHT = 88;
const VISIBLE_MESSAGE_COUNT = 4;
const PANEL_HEIGHT =
  PANEL_HEADER_HEIGHT + NOTIFICATION_ITEM_HEIGHT * VISIBLE_MESSAGE_COUNT;
const PANEL_MARGIN = 8;

interface PanelPosition {
  top: number;
  left: number;
  width: number;
  height: number;
}

function computePanelPosition(
  trigger: HTMLElement,
  variant: NotificationInboxVariant,
  menuOpen: boolean
): PanelPosition {
  const rect = trigger.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const width = Math.min(
    variant === 'sidebar' && menuOpen
      ? Math.max(rect.width, PANEL_WIDTH)
      : PANEL_WIDTH,
    vw - PANEL_MARGIN * 2
  );

  let left = rect.left;
  if (left + width > vw - PANEL_MARGIN) {
    left = vw - width - PANEL_MARGIN;
  }
  if (left < PANEL_MARGIN) left = PANEL_MARGIN;

  const preferredHeight = PANEL_HEIGHT;

  // Open above the trigger button
  let top = rect.top - preferredHeight - PANEL_MARGIN;
  let height = preferredHeight;

  if (top < PANEL_MARGIN) {
    // Not enough room above — open below
    top = rect.bottom + PANEL_MARGIN;
    const spaceBelow = vh - top - PANEL_MARGIN;
    if (spaceBelow >= preferredHeight * 0.6) {
      height = Math.min(preferredHeight, spaceBelow);
    } else {
      top = PANEL_MARGIN;
      height = Math.min(preferredHeight, rect.top - PANEL_MARGIN * 2);
    }
  }

  height = Math.max(PANEL_HEADER_HEIGHT + NOTIFICATION_ITEM_HEIGHT, height);

  return { top, left, width, height };
}

interface NotificationInboxProps {
  variant?: NotificationInboxVariant;
  /** For sidebar variant: matches collapsed/expanded menu */
  menuOpen?: boolean;
}

const NotificationInbox: React.FC<NotificationInboxProps> = ({
  variant = 'toolbar',
  menuOpen = true,
}) => {
  const { t } = useTranslation('Notifications');
  const { t: tScheduling } = useTranslation('Scheduling');
  const language = i18n.resolvedLanguage || i18n.language;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [showAllNotifications, setShowAllNotifications] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [panelPos, setPanelPos] = useState<PanelPosition | null>(null);

  const updatePanelPosition = useCallback(() => {
    if (!triggerRef.current) return;
    setPanelPos(computePanelPosition(triggerRef.current, variant, menuOpen));
  }, [variant, menuOpen]);

  useEffect(() => {
    if (!open) {
      setPanelPos(null);
      setShowAllNotifications(false);
      return;
    }
    updatePanelPosition();
    window.addEventListener('resize', updatePanelPosition);
    window.addEventListener('scroll', updatePanelPosition, true);
    return () => {
      window.removeEventListener('resize', updatePanelPosition);
      window.removeEventListener('scroll', updatePanelPosition, true);
    };
  }, [open, updatePanelPosition]);

  const recentListParams = NOTIFICATION_RECENT_LIST_PARAMS;

  const {
    data: recentListData,
    isLoading: isRecentLoading,
    isFetching: isRecentFetching,
    refetch: refetchRecentList,
  } = useQuery({
    queryKey: notificationKeys.list(recentListParams),
    queryFn: () => notificationService.list(recentListParams),
    staleTime: 30_000,
  });

  useEffect(() => {
    if (open) {
      void refetchRecentList();
    }
  }, [open, refetchRecentList]);

  const allListParams = {
    scope: 'all' as const,
    limit: 100,
    offset: 0,
  };

  const { data: allListData, isLoading: isAllLoading, isFetching: isAllFetching } =
    useQuery({
      queryKey: notificationKeys.list(allListParams),
      queryFn: () => notificationService.list(allListParams),
      enabled: open && showAllNotifications,
      staleTime: 0,
    });

  const visibleNotifications = showAllNotifications
    ? (allListData?.items ?? [])
    : (recentListData?.items ?? []);
  const totalAll = showAllNotifications
    ? (allListData?.total_all ?? allListData?.total_count ?? 0)
    : (recentListData?.total_all ?? recentListData?.total_count ?? 0);
  const isListLoading = showAllNotifications
    ? isAllLoading || (isAllFetching && !allListData)
    : isRecentLoading || (isRecentFetching && !recentListData);
  const hasOlderNotifications =
    !showAllNotifications && totalAll > visibleNotifications.length;

  const { data: unreadCount = 0 } = useQuery({
    queryKey: notificationKeys.unread(NOTIFICATION_RECENT_HOURS),
    queryFn: () => notificationService.unreadCount(NOTIFICATION_RECENT_HOURS),
    staleTime: 30_000,
  });

  const handleShowAll = () => {
    void queryClient.invalidateQueries({
      queryKey: notificationKeys.list(allListParams),
    });
    setShowAllNotifications(true);
  };

  const markReadMutation = useMutation({
    mutationFn: notificationService.markRead,
    onMutate: (notificationId: string) => {
      markNotificationReadInCache(queryClient, notificationId);
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (
        target instanceof Element &&
        target.closest('[aria-label="Notifications"][role="dialog"]')
      ) {
        return;
      }
      setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const handleSelect = (item: AppNotification) => {
    if (!item.is_read) {
      markReadMutation.mutate(item.id);
    }
    setOpen(false);
    navigate(getNotificationNavigationPath(item));
  };

  const isSidebar = variant === 'sidebar';
  const isCompact = variant === 'compact';

  const triggerButton = (
    <button
      ref={triggerRef}
      type="button"
      onClick={() => setOpen((v) => !v)}
      title={t('title')}
      aria-label={
        unreadCount
          ? t('ariaLabelUnread', { count: unreadCount })
          : t('title')
      }
      aria-expanded={open}
      className={cn(
        'relative transition-colors',
        isSidebar &&
          cn(
            'flex items-center gap-4 h-11 w-full rounded-lg',
            menuOpen ? 'px-4' : 'px-0 justify-center',
            open
              ? 'bg-primary-dui/10 text-primary-dui dark:bg-primary-dui/20'
              : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
          ),
        !isSidebar &&
          cn(
            'inline-flex items-center justify-center rounded-xl border shadow-sm h-10',
            'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800',
            'text-gray-600 dark:text-gray-200 hover:border-primary-dui hover:text-primary-dui dark:hover:border-primary-dui',
            isCompact ? 'w-10' : 'w-10 px-0 sm:w-auto sm:min-w-[10rem] sm:px-3 sm:gap-2'
          )
      )}
    >
      <span className="relative inline-flex shrink-0">
        <i className="ri-notification-3-line text-xl leading-none" aria-hidden />
        {unreadCount > 0 && (
          <span
            className={cn(
              'absolute min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none',
              isSidebar ? '-top-1 -right-1' : '-top-2 -right-2 border-2 border-white dark:border-gray-800'
            )}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </span>
      {isSidebar && menuOpen && (
        <span className="text-sm whitespace-nowrap">{t('title')}</span>
      )}
      {!isSidebar && !isCompact && (
        <span className="hidden sm:inline text-sm font-semibold whitespace-nowrap ml-2">
          {t('title')}
        </span>
      )}
    </button>
  );

  const panel =
    open && panelPos
      ? createPortal(
          <div
            role="dialog"
            aria-label={t('title')}
            className={cn(
              'fixed z-[9999] bg-card-color-100 dark:bg-gray-800 rounded-xl shadow-2xl shadow-black/10 dark:shadow-black/40',
              'border border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden'
            )}
            style={{
              top: panelPos.top,
              left: panelPos.left,
              width: panelPos.width,
              height: panelPos.height,
            }}
          >
            <div className="h-14 px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between shrink-0 bg-gray-50/80 dark:bg-gray-900/50">
              <div>
                <p className="text-sm font-semibold text-gray-800 dark:text-white">
                  {t('title')}
                </p>
                {unreadCount > 0 ? (
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">
                    {t('unreadLast24h', { count: unreadCount })}
                  </p>
                ) : (
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">
                    {showAllNotifications ? t('allNotifications') : t('last24Hours')}
                  </p>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-[11px] font-semibold h-auto py-1 px-2 text-blue-600 dark:text-blue-400"
                onClick={() => {
                  setOpen(false);
                  navigate('/exams');
                }}
              >
                {t('exams')}
              </Button>
            </div>

            <div className="overflow-y-auto overscroll-contain custom-scrollbar flex-1 min-h-0">
              {isListLoading ? (
                <p className="p-4 text-xs text-gray-400 dark:text-gray-500 text-center">{t('loading')}</p>
              ) : visibleNotifications.length === 0 ? (
                <p className="p-6 text-xs text-gray-400 dark:text-gray-500 text-center leading-relaxed">
                  {showAllNotifications ? t('emptyAll') : t('emptyRecent')}
                  <br />
                  {!showAllNotifications && hasOlderNotifications && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="mt-3 text-[11px] font-semibold h-auto py-1 text-blue-600 dark:text-blue-400"
                      onClick={handleShowAll}
                    >
                      {t('seeAll', { count: totalAll })}
                    </Button>
                  )}
                </p>
              ) : (
                visibleNotifications.map((item) => {
                  const patientName = getNotificationPatientName(item);
                  const body = formatNotificationContent(item, t, {
                    tScheduling,
                    language,
                    showPatientSeparately: Boolean(patientName),
                  });
                  return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleSelect(item)}
                    className={cn(
                      'w-full text-left px-4 py-3 border-b border-gray-100 dark:border-gray-700',
                      'hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors',
                      'min-h-[88px] box-border',
                      !item.is_read &&
                        'bg-blue-50/60 dark:bg-blue-900/20 dark:border-l-2 dark:border-l-blue-500'
                    )}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <span
                        className={cn(
                          'text-[10px] font-bold uppercase tracking-wide',
                          item.category === 'scheduling'
                            ? 'text-blue-600 dark:text-blue-500'
                            : item.category === 'dicom'
                              ? 'text-emerald-600 dark:text-emerald-400'
                              : item.category === 'prediction'
                                ? 'text-violet-600 dark:text-violet-400'
                                : item.category === 'collaboration' ||
                                    item.category === 'clinical_collaboration'
                                  ? 'text-amber-600 dark:text-amber-400'
                                  : 'text-gray-500 dark:text-gray-400'
                        )}
                      >
                        {notificationTypeLabel(item.type, t)}
                        <span className="font-normal normal-case text-gray-400 dark:text-gray-500 ml-1">
                          · {notificationCategoryLabel(item.category, t)}
                        </span>
                      </span>
                      <span className="text-[10px] text-gray-400 dark:text-gray-500 shrink-0">
                        {formatNotificationTimestamp(
                          item.created_at,
                          language,
                          t('timestampFormat')
                        )}
                      </span>
                    </div>
                    {patientName && (
                      <p className="text-xs font-semibold text-gray-900 dark:text-white mb-0.5 truncate">
                        {patientName}
                      </p>
                    )}
                    <p className="text-xs text-gray-700 dark:text-gray-400 leading-relaxed line-clamp-2">
                      {body}
                    </p>
                  </button>
                  );
                })
              )}
            </div>

            {(hasOlderNotifications || showAllNotifications) && (
              <div className="h-10 px-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-center shrink-0 bg-gray-50/80 dark:bg-gray-900/50">
                {showAllNotifications ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-[11px] font-semibold h-auto py-1 text-blue-600 dark:text-blue-400"
                    onClick={() => setShowAllNotifications(false)}
                  >
                    {t('showLast24h')}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-[11px] font-semibold h-auto py-1 text-blue-600 dark:text-blue-400"
                    onClick={handleShowAll}
                  >
                    {t('seeAll', { count: totalAll })}
                  </Button>
                )}
              </div>
            )}
          </div>,
          document.body
        )
      : null;

  return (
    <div
      className={cn('relative', isSidebar ? 'w-full' : 'inline-block')}
      ref={rootRef}
    >
      {triggerButton}
      {panel}
    </div>
  );
};

export default NotificationInbox;
